import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  AccountType,
  MembershipType,
  StripeSetupIntentType,
  API_ERROR_MAP,
} from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { flashErrorMessage } from 'redux-flash'
import { SectionHeader } from 'components'
import { MembershipPageHeader } from 'memberships/components'
import { NextInvoice } from '../components'
import AutoRenewalPaymentSetup from '../components/AutoRenewalPaymentSetup'
import { selectors } from 'memberships/reducer'
import { selectors as memberSelectors } from 'member-reducer'
import * as apiActions from 'api-actions'
import * as actions from 'member-actions'
import { PATH, makeMembershipIdPath } from 'config'
import { PAYMENT_STAGE, logPaymentFailed } from 'uxcam-service'
import {
  calculateArbStartDate,
  getPaymentSubmitErrorMessage,
  withApiAuth,
} from 'utils'

const propTypes = {
  account: AccountType.isRequired,
  clearStripeSetupIntent: PropTypes.func.isRequired,
  enrollStripeAutoRenewal: PropTypes.func.isRequired,
  fetchAllMembershipsForAccount: PropTypes.func.isRequired,
  fetchStripeSetupIntent: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  membership: MembershipType.isRequired,
  stripeCustomerId: PropTypes.string,
  stripeSetupIntent: StripeSetupIntentType,
}

function AutoRenewEnrollment({
  account,
  clearStripeSetupIntent,
  enrollStripeAutoRenewal,
  fetchAllMembershipsForAccount,
  fetchStripeSetupIntent,
  flashErrorMessage,
  membership,
  stripeCustomerId = null,
  stripeSetupIntent = null,
}) {
  const history = useHistory()

  const {
    communication_member_renewal_amount__c: renewalCost,
    sfid: membershipId,
    membership_expiration_date__c: membershipExpirationDateString,
    account__r__heroku_external_id__c,
    membership_contact__r__heroku_external_id__c: contactId,
    heroku_external_id__c,
  } = membership

  const { sfid: accountId } = account
  const arbStartDateString = calculateArbStartDate(
    membershipExpirationDateString,
  )

  const stripeSetupIntentId = stripeSetupIntent?.client_secret
  const customerSessionClientSecret =
    stripeSetupIntent?.customer_session_client_secret

  // Always replace the intent rather than reusing one: a SetupIntent is
  // single-use, so anything left from an earlier attempt is already spent.
  // Clearing first also keeps <Elements> from mounting against a stale secret,
  // which Stripe would then refuse to swap out.
  const loadSetupIntent = useCallback(
    async ({ silent = false } = {}) => {
      if (!stripeCustomerId) return

      try {
        await clearStripeSetupIntent()
        await fetchStripeSetupIntent({ stripeCustomerId })
      } catch (error) {
        logPaymentFailed(PAYMENT_STAGE.SETUP_INTENT, error)
        // Callers recovering from a failed submit already have an error to show,
        // and a second banner would only bury the one that matters.
        if (!silent) flashErrorMessage(API_ERROR_MAP.STRIPE_SETUP_INTENT_FAILED)
      }
    },
    [
      clearStripeSetupIntent,
      fetchStripeSetupIntent,
      flashErrorMessage,
      stripeCustomerId,
    ],
  )

  useEffect(() => {
    loadSetupIntent()
    // Don't strand a spent intent for the billing screen to pick up. Swallow the
    // rejection: withApiAuth makes this an async token fetch, and an unmount is
    // no place to surface that.
    return () => {
      clearStripeSetupIntent().catch(() => {})
    }
  }, [loadSetupIntent, clearStripeSetupIntent])

  const buildBasePayload = () => ({
    membershipid: membershipId,
    amount: renewalCost,
    account__r__heroku_external_id__c,
    contact__r__heroku_external_id__c: contactId,
    membership__r__heroku_external_id__c: heroku_external_id__c,
    arb_start_date: arbStartDateString,
  })

  const handleSubmit = async (formValues) => {
    const { stripe_payment_method_id } = formValues
    const basePayload = buildBasePayload()

    // Reaching this function means confirmSetupIntent already succeeded, so the
    // intent is spent.
    let response
    try {
      response = await enrollStripeAutoRenewal({
        ...basePayload,
        stripe_customer_id__c: stripeCustomerId,
        stripe_payment_method_id,
      })
    } catch (error) {
      logPaymentFailed(PAYMENT_STAGE.AUTO_RENEW_ENROLLMENT, error)
      // Replace the spent intent instead of only discarding it. This view has no
      // read-only state to fall back to, so an empty intent would leave the
      // member on a permanent spinner with no way to retry. Deliberately not
      // awaited, so the submit error surfaces immediately rather than after a
      // clear + refetch round trip; loadSetupIntent handles its own failures.
      loadSetupIntent({ silent: true })
      throw error
    }

    // Enrollment succeeded, so always land the member back on Billing -- even if
    // the refresh below fails. Navigating unmounts this view, which discards the
    // spent intent.
    try {
      await fetchAllMembershipsForAccount(accountId)
    } finally {
      history.replace(makeMembershipIdPath(membershipId, PATH.BILLING))
    }

    return response
  }

  const handleSubmitFail = (error) => {
    flashErrorMessage(getPaymentSubmitErrorMessage(error))
  }

  const handleCloseForm = () => {
    history.replace(makeMembershipIdPath(membershipId, PATH.BILLING))
  }

  return (
    <>
      <MembershipPageHeader
        backLinkText="Back to Billing"
        backLinkTo={makeMembershipIdPath(membershipId, PATH.BILLING)}
        membership={membership}
      />
      <section className="form-block-container">
        <div className="card card-medium">
          <div className="card-inner">
            <div className="form-block">
              <SectionHeader
                sectionHeaderName="Enroll in Auto Renewal"
                sectionDescription="Auto Renewal gives you peace of mind that your membership won’t expire."
              />
              <div className="group-block">
                <NextInvoice
                  autorenews
                  renewalCost={renewalCost}
                  invoiceTermDate={arbStartDateString}
                  withInvoiceTerms
                />
                <hr />
              </div>
              <AutoRenewalPaymentSetup
                stripeSetupIntentId={stripeSetupIntentId}
                customerSessionClientSecret={customerSessionClientSecret}
                onSubmit={handleSubmit}
                onError={handleSubmitFail}
                closeEditForm={handleCloseForm}
                submitButtonText="Submit"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

AutoRenewEnrollment.propTypes = propTypes

function mapStateToProps(state) {
  return {
    account: selectors.account(state),
    membership: memberSelectors.membership(state),
    stripeCustomerId: selectors.stripeCustomerId(state),
    stripeSetupIntent: memberSelectors.stripeSetupIntent(state),
  }
}

const mapDispatchToProps = {
  flashErrorMessage,
}

const mapApiAuthToProps = {
  clearStripeSetupIntent: actions.clearStripeSetupIntent,
  enrollStripeAutoRenewal: apiActions.enrollStripeAutoRenewal,
  fetchAllMembershipsForAccount: apiActions.fetchAllMembershipsForAccount,
  fetchStripeSetupIntent: apiActions.fetchStripeSetupIntent,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withApiAuth(mapApiAuthToProps),
)(AutoRenewEnrollment)
